/* eslint-disable jira/i18n/no-export-define-message */
import React, { useEffect, useMemo } from 'react';
import type { RovoConfigProviderEntryProps } from '@jira/ai-rovo_actions-shared-types/src/RovoConfigProviderEntry.types.tsx';
import type { RovoConfiguration } from '@jira/ai-rovo_actions-shared-types/src/RovoConfiguration.types.tsx';
import type { RovoAction } from '@jira/ai-rovo_actions-shared-types/src/RovoAction.types.tsx';
import AiChatIcon from '@atlaskit/icon/core/ai-chat';
import { token } from '@atlaskit/tokens';
import { defineMessage, type MessageDescriptor, useIntl } from '@atlassian/jira-intl';
import { HandleActionType } from '@atlassian/rovo-conversation-actions/types';
import type { SpacePermissionConfigProviderEntryEntrypointProps } from './SpacePermissionConfigProviderEntry.entrypoint.types.tsx';
import { useExplicitPermissionsSkillPrompt } from './useExplicitPermissionsSkillPrompt.tsx';

export const whoCanBrowsePrompt: MessageDescriptor = defineMessage({
	defaultMessage: 'Who can browse this space?',
	description: 'Permissions AI prompt Who can browse this space?',
	id: 'ai-rovo.permissions.who-can-browse',
});

export const whoCanCreateWorkItemsPrompt: MessageDescriptor = defineMessage({
	defaultMessage: 'Who can create work items in this space?',
	description: 'Permissions AI prompt Who can create work items in this space?',
	id: 'ai-rovo.permissions.who-can-create-work-items',
});

export const whoHasAdministerSpacesPermissionPrompt: MessageDescriptor = defineMessage({
	defaultMessage:
		'Which users, groups or roles have the Administer Spaces permission in this space?',
	description:
		'Permissions AI prompt Which users, groups or roles have the Administer Spaces permission in this space?',
	id: 'ai-rovo.permissions.who-has-administer-spaces-permission',
});

/**
 * Rovo actions - company managed space permission prompts.
 */
export const SpacePermissionConfigProvider = ({
	setConfiguration,
}: SpacePermissionConfigProviderEntryEntrypointProps): null => {
	const { formatMessage } = useIntl();
	const { formatExplicitPermissionsSkillPrompt } = useExplicitPermissionsSkillPrompt();

	const config = useMemo<RovoConfiguration>(() => {
		return {
			getActions: async () => {
				const actions: RovoAction[] = [
					{
						action: {
							id: 'who-can-browse-space',
							title: formatMessage(whoCanBrowsePrompt),
							staticPrompt: formatExplicitPermissionsSkillPrompt(whoCanBrowsePrompt),
							icon: () => <AiChatIcon label="" color={token('color.icon')} />,
							handleType: HandleActionType.Skill,
						},
					},
					{
						action: {
							id: 'who-can-create-work-items',
							title: formatMessage(whoCanCreateWorkItemsPrompt),
							staticPrompt: formatExplicitPermissionsSkillPrompt(whoCanCreateWorkItemsPrompt),
							icon: () => <AiChatIcon label="" color={token('color.icon')} />,
							handleType: HandleActionType.Skill,
						},
					},
					{
						action: {
							id: 'who-has-administer-spaces-permission',
							title: formatMessage(whoHasAdministerSpacesPermissionPrompt),
							staticPrompt: formatExplicitPermissionsSkillPrompt(
								whoHasAdministerSpacesPermissionPrompt,
							),
							icon: () => <AiChatIcon label="" color={token('color.icon')} />,
							handleType: HandleActionType.Skill,
						},
					},
				];

				return Promise.all(
					actions.map(async ({ action, entryPointActions }) => {
						await entryPointActions?.load();
						return action;
					}),
				);
			},
			tags: {
				skillRegistryTags: ['jira-permissions'],
			},
		};
	}, [formatMessage, formatExplicitPermissionsSkillPrompt]);

	useEffect(() => {
		// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
		setConfiguration(config);
	}, [config, setConfiguration]);

	return null;
};

export const SpacePermissionConfigProviderEntry = ({
	props: { setConfiguration },
}: RovoConfigProviderEntryProps): React.JSX.Element => (
	<SpacePermissionConfigProvider setConfiguration={setConfiguration} />
);

export default SpacePermissionConfigProviderEntry;
